import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Smartphone, Monitor, Apple, Check, Zap, Share } from "lucide-react";
import { Modal } from "./kit";

const Step = ({ n, children }: { n: number; children: React.ReactNode }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold grid place-items-center">{n}</span>
    <span className="text-sm text-slate-700 leading-relaxed">{children}</span>
  </li>
);

const DeviceCard = ({ icon: Icon, title, children }: any) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2 mb-3">
      <span className="w-9 h-9 rounded-xl grid place-items-center bg-blue-50 text-blue-700"><Icon size={18} /></span>
      <h4 className="font-semibold text-slate-900">{title}</h4>
    </div>
    <ol className="space-y-2.5">{children}</ol>
  </div>
);

export default function InstallApp({ variant = "header" }: { variant?: "header" | "welcome" }) {
  const [deferred, setDeferred] = useState<any>(null);
  const [installed, setInstalled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone;
    if (standalone) setInstalled(true);
    const onBIP = (e: any) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setOpen(false); toast.success("অ্যাপ ইনস্টল হয়েছে — এবার থেকে হোম স্ক্রিন থেকে খুলুন"); };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const click = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (choice?.outcome !== "accepted") setOpen(true);
    } else {
      setOpen(true);
    }
  };

  const trigger =
    variant === "welcome" ? (
      <button onClick={click} data-testid="install-app-button"
        className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-white/10 border border-white/15 text-white hover:bg-white/15 backdrop-blur font-semibold transition-all active:scale-95">
        <Download size={18} /> অ্যাপ ডাউনলোড করুন
      </button>
    ) : (
      <button onClick={click} data-testid="install-app-button" className="btn btn-ghost btn-sm" title="অ্যাপ ডাউনলোড করুন">
        <Download size={16} /> <span className="hidden lg:inline">অ্যাপ ডাউনলোড</span>
      </button>
    );

  return (
    <>
      {trigger}
      <Modal open={open} onClose={() => setOpen(false)} title="রানা ইলেক্ট্রিক অ্যাপ ডাউনলোড" wide testid="install-app-modal">
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 text-white p-4">
            <span className="w-12 h-12 rounded-xl bg-white/15 grid place-items-center ring-1 ring-white/20"><Zap size={24} className="text-amber-300" fill="currentColor" /></span>
            <div>
              <p className="font-bold text-[17px]">আলাদা অ্যাপের মতো ব্যবহার করুন</p>
              <p className="text-sm text-blue-100">হোম স্ক্রিনে আইকন বসবে, ফুল-স্ক্রিনে খুলবে — ইন্টারনেট ছাড়াও আগের পেজ দেখা যাবে।</p>
            </div>
          </div>

          {deferred && (
            <button onClick={click} data-testid="install-now-button"
              className="w-full h-12 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-500 transition-colors inline-flex items-center justify-center gap-2">
              <Download size={18} /> এখনই ইনস্টল করুন
            </button>
          )}

          <div className="grid sm:grid-cols-3 gap-3">
            <DeviceCard icon={Smartphone} title="Android ফোন">
              <Step n={1}>Chrome ব্রাউজারে ওয়েবসাইটটি খুলুন</Step>
              <Step n={2}>উপরে ডান দিকের মেনু (<b>⋮</b>) চাপুন</Step>
              <Step n={3}>“<b>Install app</b>” বা “Add to Home screen” বেছে নিন</Step>
              <Step n={4}><b>Install</b> চাপুন — হোম স্ক্রিনে আইকন বসবে</Step>
            </DeviceCard>
            <DeviceCard icon={Apple} title="iPhone / iPad">
              <Step n={1}><b>Safari</b> ব্রাউজারে সাইটটি খুলুন</Step>
              <Step n={2}>নিচের <b>Share</b> <Share size={13} className="inline -mt-0.5" /> বাটন চাপুন</Step>
              <Step n={3}>“<b>Add to Home Screen</b>” বেছে নিন</Step>
              <Step n={4}><b>Add</b> চাপুন</Step>
            </DeviceCard>
            <DeviceCard icon={Monitor} title="কম্পিউটার (PC)">
              <Step n={1}>Chrome / Edge-এ সাইটটি খুলুন</Step>
              <Step n={2}>অ্যাড্রেস বারের ডান পাশে <b>ইনস্টল</b> <Download size={13} className="inline -mt-0.5" /> আইকন চাপুন</Step>
              <Step n={3}><b>Install</b> চাপুন — ডেস্কটপে অ্যাপ হিসেবে খুলবে</Step>
            </DeviceCard>
          </div>

          <p className="flex items-center gap-2 text-xs text-slate-500">
            <Check size={14} className="text-emerald-600" /> সম্পূর্ণ ফ্রি • কোনো প্লে-স্টোর লাগবে না • নতুন আপডেট নিজে থেকেই আসবে
          </p>
        </div>
      </Modal>
    </>
  );
}
